/**
 * PlanRendererHotSpotMode — renders the floor plan SVG in HotSpot mode.
 *
 * Responsibilities:
 *   - Mount the cached SVG clone into the container.
 *   - Inject interactive hotspot symbols at camera positions (interior_* cameras).
 *   - Calculate rotation angle for each hotspot based on camera target.
 *   - Handle hotspot click → open modal with interior image.
 *   - Support both 2D and 3D viewer variants.
 */

import { useEffect, useRef, useMemo, useCallback, useState } from 'react'

import { useApp }     from '../../../../app/AppContext.jsx'
import { useCatalog } from '../../../catalog/index.js'
import { useProduct } from '../../index.js'

import { VIEWER_VARIANT } from '../../constants/viewerVariant.js'
import { COLOR_SCHEME } from '../../constants/colorSchemes.js'

import { applyRenderTopMutations } from '../../utils/svgRenderTop.js'
import { applyColorSchemeMutations } from '../../utils/svgColorScheme.js'
import { attachStrokeGhost, removeStrokeGhost } from '../../utils/svgGhostStroke.js'
import { usePlanNorthRotation } from '../../hooks/usePlanNorthRotation.js'
import { HotSpotModal } from './HotSpotModal.jsx'

import styles from './PlanRendererHotSpotMode.module.scss'

const SVG_NS = 'http://www.w3.org/2000/svg'
const HOTSPOTS_LAYER_ID = 'aplus-hotspots'

const CLASS_NAMES = {
  compartmentHovered:     styles.compartmentHovered,
  compartmentSelected:    styles.compartmentSelected,
  compartmentNotSelected: styles.compartmentNotSelected,
}

// ── Hotspot symbol dimensions (SVG units) ────────────────────────────────────

const HOTSPOT_RADIUS = 24
const HOTSPOT_SECTOR_RADIUS = 96

// ── Helper: calculate rotation angle for hotspot symbol ──────────────────────

/**
 * calculateRotationAngle — computes the rotation angle (degrees) for a hotspot symbol
 * based on camera position and target, so the FOV sector points toward the target.
 *
 * Coordinate system: Y-DOWN (SVG standard), angle relative to positive X-axis.
 *
 * @param {[number, number]} target — target point [x, y]
 * @param {[number, number]} position — camera position [x, y]
 * @returns {number} — rotation angle in degrees
 */
function calculateRotationAngle(target, position) {
  const [targetX, targetY] = target
  const [positionX, positionY] = position

  // Flip Y for cartesian calculation (Y-UP)
  const deltaX = targetX - positionX
  const deltaY = -(targetY - positionY)

  const angleRadians = Math.atan2(deltaY, deltaX)
  const angleDegrees = angleRadians * (180 / Math.PI)

  // Subtract 30° because the sector is 60° wide and we want it centered on the target direction.
  // This assumes the SVG symbol is designed to point right (0°) when unrotated.
  return angleDegrees - 30
}

// ── Helper: build hotspot symbol template ────────────────────────────────────

/**
 * _buildHotspotSymbol — creates a reusable <g> element representing one hotspot.
 *
 * Structure:
 *   - FOV sector (60° arc) pointing right (0°), will be rotated per camera
 *   - 3 concentric circles: outer (pulsating), middle (ring), inner (filled center)
 *
 * @returns {SVGGElement}
 */
function _buildHotspotSymbol() {
  const group = document.createElementNS(SVG_NS, 'g')

  // 1. FOV sector (60° arc, pointing right)
  const sector = document.createElementNS(SVG_NS, 'path')
  const endX = HOTSPOT_SECTOR_RADIUS / 2
  const endY = HOTSPOT_SECTOR_RADIUS * Math.sin((60 * Math.PI) / 180)
  sector.setAttribute(
    'd',
    `M 0 0 L ${HOTSPOT_SECTOR_RADIUS} 0 A ${HOTSPOT_SECTOR_RADIUS} ${HOTSPOT_SECTOR_RADIUS} 0 0 1 ${endX} ${endY} Z`
  )
  sector.setAttribute('class', styles.hotspotSector)
  group.appendChild(sector)

  // 2. Outer circle (pulsating animation)
  const circle1 = document.createElementNS(SVG_NS, 'circle')
  circle1.setAttribute('cx', '0')
  circle1.setAttribute('cy', '0')
  circle1.setAttribute('r', String(HOTSPOT_RADIUS))
  circle1.setAttribute('class', styles.hotspotCircle)

  const animate = document.createElementNS(SVG_NS, 'animate')
  animate.setAttribute('attributeName', 'r')
  animate.setAttribute('values', `${HOTSPOT_RADIUS};${HOTSPOT_RADIUS * 1.5};${HOTSPOT_RADIUS}`)
  animate.setAttribute('dur', '2s')
  animate.setAttribute('repeatCount', 'indefinite')
  circle1.appendChild(animate)
  group.appendChild(circle1)

  // 3. Middle circle (static ring)
  const circle2 = document.createElementNS(SVG_NS, 'circle')
  circle2.setAttribute('cx', '0')
  circle2.setAttribute('cy', '0')
  circle2.setAttribute('r', String(HOTSPOT_RADIUS * 0.75))
  circle2.setAttribute('class', styles.hotspotRing)
  group.appendChild(circle2)

  // 4. Inner circle (filled center)
  const circle3 = document.createElementNS(SVG_NS, 'circle')
  circle3.setAttribute('cx', '0')
  circle3.setAttribute('cy', '0')
  circle3.setAttribute('r', String(HOTSPOT_RADIUS * 0.5))
  circle3.setAttribute('class', styles.hotspotCenter)
  group.appendChild(circle3)

  return group
}

// ── Helper: inject hotspots layer into SVG ───────────────────────────────────

/**
 * _injectHotspotsLayer — appends a <g> layer with hotspot symbols to the SVG.
 *
 * Each hotspot is positioned at the camera's position and rotated to point at the target.
 *
 * @param {SVGElement} svgElement — the cloned SVG root
 * @param {Array<{ name: string, position: [number, number], target: [number, number] }>} hotspots
 */
function _injectHotspotsLayer(svgElement, hotspots) {
  // Append directly to SVG root (highest level) to avoid transform inheritance issues

  // Remove existing layer if present (for idempotency in dev hot-reload)
  const existingLayer = svgElement.getElementById(HOTSPOTS_LAYER_ID)
  if (existingLayer) existingLayer.remove()

  const hotspotsGroup = document.createElementNS(SVG_NS, 'g')
  hotspotsGroup.setAttribute('id', HOTSPOTS_LAYER_ID)

  hotspots.forEach((spot) => {
    const symbol = _buildHotspotSymbol()
    symbol.setAttribute('class', `${styles.hotspotSymbol} aplus-hotspot-symbol`)
    symbol.setAttribute('data-name', spot.name)

    const angle = calculateRotationAngle(spot.target, spot.position)
    const [x, y] = spot.position
    // Flip Y coordinate to match SVG Y-DOWN coordinate system
    symbol.setAttribute('transform', `translate(${x} ${-y}) rotate(${angle})`)

    hotspotsGroup.appendChild(symbol)
  })

  svgElement.appendChild(hotspotsGroup)
}

/**
 * @param {{ svgElement: SVGElement|null, svgDimensions: { width: number, height: number } }} props
 */
export function PlanRendererHotSpotMode({ svgElement, svgDimensions }) {
  const containerRef    = useRef(null)
  const mountedSvgRef   = useRef(null)
  const wrapperRef      = useRef(null)
  const hoverGhostRef   = useRef(null)
  const selectedGhostRef = useRef(null)

  const { windowData, isMobile, panelSettings } = useApp()
  const { interiorsData } = useCatalog()
  const { viewerState, viewerActions, selection } = useProduct()
  const { viewerVariant, selectedLevelId, rotatedToNorth, hoveredCompartmentId, selectedCompartmentId } = viewerState
  const { setHoveredCompartmentId, setSelectedCompartmentId } = viewerActions
  const { product } = selection

  // ── Local state ───────────────────────────────────────────────────────────

  const [selectedHotspotId, setSelectedHotspotId] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // ── Rotate-to-North ───────────────────────────────────────────────────────

  const { wrapperStyle, rendererStyle } = usePlanNorthRotation({
    rotatedToNorth,
    azimuth: product?.azimuth ?? null,
    svgDimensions,
    wrapperRef,
  })

  // ── Derived: active tier and filtered cameras ─────────────────────────────

  const refTier = selectedLevelId ?? product?.tiers?.[0]?.ref_tier ?? null
  const tier = useMemo(() => {
    if (!product || refTier == null) return null
    return product.tiers?.find((t) => t.ref_tier === refTier) ?? null
  }, [product, refTier])

  // Filter cameras: interior_* cameras with interiors.json entries.
  const apartmentHotSpots = useMemo(() => {
    if (!tier || !interiorsData) return []

    const cameras = tier.cameras ?? []
    return cameras.filter((camera) => {
      const name = camera?.name ?? ''
      if (!name.toLowerCase().startsWith('interior_')) return false
      const files = interiorsData[name]
      return Array.isArray(files) && files.length > 0
    })
  }, [tier, interiorsData])

  const selectedHotspot = useMemo(
    () => apartmentHotSpots.find((h) => h.name === selectedHotspotId) ?? null,
    [apartmentHotSpots, selectedHotspotId],
  )

  const is3D = viewerVariant === VIEWER_VARIANT._3D

  // ── Pointer handlers: compartment hover / click ────────────────────────

  const handlePointerMove = useCallback((event) => {
    if (event.pointerType === 'touch') return
    const path = event.target?.closest('path.compartment')
    const n = path ? Number(path.getAttribute('number')) : null
    setHoveredCompartmentId(n != null && Number.isFinite(n) ? n : null)
  }, [setHoveredCompartmentId])

  const handlePointerLeave = useCallback(() => {
    setHoveredCompartmentId(null)
  }, [setHoveredCompartmentId])

  const handleCompartmentClick = useCallback((event) => {
    const path = event.target?.closest('path.compartment')
    if (!path) return
    const n = Number(path.getAttribute('number'))
    if (!Number.isFinite(n)) return
    setSelectedCompartmentId((prev) => (prev === n ? null : n))
  }, [setSelectedCompartmentId])

  // ── Click handler: hotspot clicked → open modal ──────────────────────────

  const handleHotspotClick = useCallback((event) => {
    const symbol = event.target.closest('g.aplus-hotspot-symbol')
    if (!symbol) return
    const hotspotName = symbol.dataset.name
    if (hotspotName) {
      setSelectedHotspotId(hotspotName)
      setIsModalOpen(true)
    }
  }, [])

  // ── Effect: Mount SVG clone with hotspots layer ──────────────────────────

  useEffect(() => {
    if (!containerRef.current || !svgElement) return

    const clone = svgElement.cloneNode(true)

    // Apply 3D mutations if needed (hide equipments/partitions, apply PNG texture)
    if (is3D) {
      applyRenderTopMutations(clone, product, windowData?.projectDataUrl ?? null)
    } else {
      // Clear any inline fills so CSS vars (MONO / Layer 1) take over
      applyColorSchemeMutations(clone, COLOR_SCHEME.MONO)
    }

    // Inject hotspots layer
    _injectHotspotsLayer(clone, apartmentHotSpots)

    // Attach click listener to hotspots
    clone.addEventListener('click', handleHotspotClick)

    containerRef.current.replaceChildren(clone)
    mountedSvgRef.current = clone

    return () => {
      clone.removeEventListener('click', handleHotspotClick)
    }
  }, [svgElement, is3D, product, windowData?.projectDataUrl, apartmentHotSpots, handleHotspotClick])

  // ── Effect: hover highlight ────────────────────────────────────────────────

  useEffect(() => {
    const svg = mountedSvgRef.current
    if (!svg) return

    const paths = svg.querySelectorAll('path.compartment')
    if (!hoveredCompartmentId) {
      if (is3D) {
        paths.forEach((p) => p.classList.remove(CLASS_NAMES.compartmentHovered))
      } else {
        removeStrokeGhost(hoverGhostRef)
      }
      return
    }

    const target = [...paths].find(
      (p) => Number(p.getAttribute('number')) === hoveredCompartmentId,
    )
    if (!target) return

    if (is3D) {
      paths.forEach((p) => p.classList.remove(CLASS_NAMES.compartmentHovered))
      target.classList.add(CLASS_NAMES.compartmentHovered)
    } else {
      target.classList.add(CLASS_NAMES.compartmentHovered)
      attachStrokeGhost(target, 'var(--bim-color-product-outline, var(--bim-color-hero-outlines))', '12', hoverGhostRef)
    }

    return () => {
      target.classList.remove(CLASS_NAMES.compartmentHovered)
      if (!is3D) removeStrokeGhost(hoverGhostRef)
    }
  }, [hoveredCompartmentId, is3D])

  // ── Effect: selected highlight ────────────────────────────────────────────

  useEffect(() => {
    const svg = mountedSvgRef.current
    if (!svg) return

    const paths = svg.querySelectorAll('path.compartment')
    if (!selectedCompartmentId) {
      if (is3D) {
        paths.forEach((p) => {
          p.classList.remove(CLASS_NAMES.compartmentSelected)
          p.classList.remove(CLASS_NAMES.compartmentNotSelected)
        })
      } else {
        removeStrokeGhost(selectedGhostRef)
      }
      return
    }

    const target = [...paths].find(
      (p) => Number(p.getAttribute('number')) === selectedCompartmentId,
    )
    if (!target) return

    if (is3D) {
      paths.forEach((p) => {
        p.classList.remove(CLASS_NAMES.compartmentSelected)
        p.classList.remove(CLASS_NAMES.compartmentNotSelected)
        p.classList.add(CLASS_NAMES.compartmentNotSelected)
      })
      target.classList.remove(CLASS_NAMES.compartmentNotSelected)
      target.classList.add(CLASS_NAMES.compartmentSelected)
    } else {
      target.classList.add(CLASS_NAMES.compartmentSelected)
      attachStrokeGhost(target, 'var(--bim-color-product-active, var(--bim-color-hero-active))', '12', selectedGhostRef)
    }

    return () => {
      target.classList.remove(CLASS_NAMES.compartmentSelected)
      paths.forEach((p) => p.classList.remove(CLASS_NAMES.compartmentNotSelected))
      if (!is3D) removeStrokeGhost(selectedGhostRef)
    }
  }, [selectedCompartmentId, is3D])

  // ── Modal config ──────────────────────────────────────────────────────────

  const modalConfig = panelSettings?.unit_sheet_mode_hotspot?.hotspot_modal

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <div ref={wrapperRef} className={styles.wrapper} style={wrapperStyle}>
        <div
          ref={containerRef}
          className={`${styles.renderer} bim-plan`}
          data-variant={viewerVariant}
          style={rendererStyle}
          onPointerMove={handlePointerMove}
          onPointerLeave={handlePointerLeave}
          onClick={handleCompartmentClick}
        />
      </div>

      <HotSpotModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        hotspot={selectedHotspot}
        isMobile={isMobile}
        modalConfig={modalConfig}
        allHotspots={apartmentHotSpots}
        onHotspotSelect={setSelectedHotspotId}
      />
    </>
  )
}
